import { useMemo, useState } from 'react'

/** Rows per page for the trucks / trailers / drivers tables. */
export const DEFAULT_TABLE_PAGE_SIZE = 25
/** A compact roster shows its search from this many rows; below it every row is in view. */
export const SEARCH_MIN_ROWS = 5

export type SearchValue = string | number | null | undefined

interface UseTableSearchOptions<T> {
  items: T[]
  /**
   * The values a row is matched against. Must be a stable reference (module-level
   * function or `useCallback`), otherwise the filter recomputes on every render.
   */
  getSearchValues: (item: T) => SearchValue[]
  pageSize?: number
}

/**
 * Client-side search + pagination over an already-loaded list.
 *
 * A query matches when every whitespace-separated term appears somewhere in the
 * row's search values, so "freightliner ca" narrows instead of finding nothing.
 * The page is derived (clamped to `totalPages`) rather than stored, so deleting
 * the last row of the last page can't leave the table showing an empty page.
 */
export function useTableSearch<T>({
  items,
  getSearchValues,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
}: UseTableSearchOptions<T>) {
  const [query, setQuery] = useState('')
  const [requestedPage, setRequestedPage] = useState(1)

  const filtered = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (terms.length === 0) return items
    return items.filter((item) => {
      const haystack = getSearchValues(item)
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join(' ')
        .toLowerCase()
      return terms.every((term) => haystack.includes(term))
    })
  }, [items, query, getSearchValues])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const page = Math.min(requestedPage, totalPages)
  const pageItems = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  )

  function onQueryChange(next: string) {
    setQuery(next)
    setRequestedPage(1)
  }

  return {
    query,
    onQueryChange,
    page,
    setPage: setRequestedPage,
    totalPages,
    /** The rows to render for the current page. */
    pageItems,
    /** Every row matching the query, across all pages. */
    filtered,
    /** Range summary for the pagination control, e.g. "1–25 of 512 trucks". */
    rangeSummary(labelPlural: string) {
      if (filtered.length === 0) return `0 ${labelPlural}`
      const start = (page - 1) * pageSize + 1
      const end = Math.min(page * pageSize, filtered.length)
      return `${start}–${end} of ${filtered.length} ${labelPlural}`
    },
  }
}
